import { ADMIN_API_URL, supabase } from './supabase';

// Typed wrapper around the tapes-admin-api Worker (Backend/CF/admin-api).
// All calls automatically attach the user's current Supabase access_token.
// Worker validates JWT + role server-side.

export class AdminApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'AdminApiError';
  }
}

export function isAdminApiConfigured(): boolean {
  return ADMIN_API_URL.length > 0;
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  if (!ADMIN_API_URL) {
    throw new AdminApiError(0, 'VITE_ADMIN_API_URL is not set. Configure .env.local + deploy the admin worker (Backend/CF/admin-api).');
  }
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new AdminApiError(401, 'Not authenticated');
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const resp = await fetch(`${ADMIN_API_URL}${path}`, { ...init, headers });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try {
      const j = await resp.json() as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep default */ }
    throw new AdminApiError(resp.status, msg);
  }
  return resp;
}

// ─────────────── Stream upload (TUS) ───────────────

export interface StreamDirectUpload {
  uploadUrl: string;  // TUS endpoint to PATCH chunks to
  uid:       string;  // CF Stream UID, persist to videos.cloudflare_uid
}

export async function getStreamDirectUpload(args: {
  filename: string;
  uploadLength: number;
  maxDurationSeconds?: number;
}): Promise<StreamDirectUpload> {
  const resp = await authedFetch('/upload/stream/direct-upload', {
    method: 'POST',
    body: JSON.stringify(args),
  });
  return resp.json();
}

// ─────────────── Images upload ───────────────

export interface ImageDirectUpload {
  uploadUrl: string;  // POST multipart/form-data 'file' here
  id:        string;  // CF Images id, persist to videos.cloudflare_image_id
}

export async function getImageDirectUpload(): Promise<ImageDirectUpload> {
  const resp = await authedFetch('/upload/images/direct-upload', { method: 'POST' });
  return resp.json();
}

export async function uploadImage(file: File): Promise<string> {
  const { uploadUrl, id } = await getImageDirectUpload();
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(uploadUrl, { method: 'POST', body: form });
  if (!resp.ok) throw new AdminApiError(resp.status, `CF Images upload failed: ${resp.statusText}`);
  return id;
}

// ─────────────── Cache purge ───────────────

export interface PurgeResult {
  status: number;
  ok: boolean;
  body: string;
}

export async function purgeFeedCache(): Promise<PurgeResult> {
  const resp = await authedFetch('/purge-cache', { method: 'POST' });
  return resp.json();
}

// ─────────────── Tape delete ───────────────

export interface DeleteTapeResult {
  ok: true;
  mixtapes_affected: Array<{ id: string; title: string }>;
  cloudflare_uid: string | null;
  cloudflare_image_id: string | null;
  /** Non-fatal CF DELETE failures (asset wasn't found or token issue). The
   *  DB row is already gone / admin can manually purge orphans in CF. */
  warnings: string[];
}

export async function deleteTape(tapeId: string): Promise<DeleteTapeResult> {
  const resp = await authedFetch('/tapes/delete', {
    method: 'POST',
    body: JSON.stringify({ tape_id: tapeId }),
  });
  return resp.json();
}

export interface DeleteTapesBulkResult {
  ok: true;
  deleted_count: number;
  mixtapes_affected: Array<{ id: string; title: string }>;
  warnings: string[];
}

export async function deleteTapesBulk(tapeIds: string[]): Promise<DeleteTapesBulkResult> {
  const resp = await authedFetch('/tapes/delete-bulk', {
    method: 'POST',
    body: JSON.stringify({ tape_ids: tapeIds }),
  });
  return resp.json();
}

// ─────────────── Image (orphan) delete ───────────────

/** Worker rejects if the image is still referenced by any user avatar or
 *  tape poster / that's the only authorization beyond auth. Surface 409s so
 *  the UI can show a meaningful message. */
export async function deleteImage(imageId: string): Promise<{ ok: true; image_id: string }> {
  const resp = await authedFetch('/images/delete', {
    method: 'POST',
    body: JSON.stringify({ image_id: imageId }),
  });
  return resp.json();
}

/** Extract the CF Images id from a delivery URL like
 *  `https://imagedelivery.net/<hash>/<image_id>/<variant>`. Returns null
 *  if the URL isn't a CF Images URL (e.g. gravatar, Apple Sign-In). */
export function extractCfImageId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/imagedelivery\.net\/[^/]+\/([^/]+)/);
  return m ? m[1] : null;
}

// ─────────────── Analytics ───────────────

export interface CfStorageStats {
  stream: {
    video_count:     number;
    total_seconds:   number;
    total_minutes:   number;
    total_hours:     number;
    total_bytes:     number;
    total_gigabytes: number;
  };
  images: {
    image_count: number;
  };
}

export async function getCfStorage(): Promise<CfStorageStats> {
  const resp = await authedFetch('/analytics/cf-storage', { method: 'GET' });
  return resp.json();
}

export interface TopViewsItem {
  uid: string;
  minutes_viewed: number;
}
export interface TopViewsResponse {
  items: TopViewsItem[];
  window_days: number;
  since: string;
}

export async function getTopViews(days = 30, limit = 10): Promise<TopViewsResponse> {
  const resp = await authedFetch(`/analytics/top-views?days=${days}&limit=${limit}`, { method: 'GET' });
  return resp.json();
}

// ─────────────── Consumption ───────────────
// What end users consume in the window: stream minutes viewed (real
// cost driver) + image storage quota (current vs allowed).
//
// stream is null when CF_ANALYTICS_TOKEN isn't set on the worker /
// images stays populated either way. Either side can carry an `error`
// field if its upstream failed independently (Promise.allSettled on
// the worker).

export interface ConsumptionStream {
  total_minutes: number;
  daily:         Array<{ date: string; minutes: number }>;
  top_uids:      Array<{ uid: string; minutes_viewed: number }>;
}
export interface ConsumptionImages {
  storage?: { current: number; allowed: number };
  error?:   string;
}
export interface ConsumptionResponse {
  window_days: number;
  since:       string;
  stream:      ConsumptionStream | { error: string } | null;
  images:      ConsumptionImages;
  notes?:      string;
}

export async function getConsumption(days = 30): Promise<ConsumptionResponse> {
  const resp = await authedFetch(`/analytics/consumption?days=${days}`, { method: 'GET' });
  return resp.json();
}

// ─────────────── Invite user ───────────────

export interface InviteUserArgs {
  email:         string;
  role:          'admin' | 'editor' | 'reviewer' | 'user';
  display_name?: string;
  handle?:       string;
}
export interface InviteUserResult {
  ok:      true;
  user_id: string;
  email:   string;
  role:    string;
}

export async function inviteUser(args: InviteUserArgs): Promise<InviteUserResult> {
  const resp = await authedFetch('/users/invite', {
    method: 'POST',
    body:   JSON.stringify(args),
  });
  return resp.json();
}

// ─────────────── Send sign-in link ───────────────

export interface SendLinkResult { ok: true; email: string; action_link: string }

export async function sendSignInLink(email: string): Promise<SendLinkResult> {
  const resp = await authedFetch('/users/send-link', {
    method: 'POST',
    body:   JSON.stringify({ email }),
  });
  return resp.json();
}

// ─────────────── Deactivate / reactivate user ───────────────

export interface DeactivateResult { ok: true; user_id: string; deactivated: boolean; warning?: string }

export async function setUserDeactivated(userId: string, deactivated: boolean): Promise<DeactivateResult> {
  const resp = await authedFetch('/users/deactivate', {
    method: 'POST',
    body:   JSON.stringify({ user_id: userId, deactivated }),
  });
  return resp.json();
}

// ─────────────── Delete mixtape ───────────────

export interface DeleteMixtapeResult {
  ok:                  true;
  mixtape_title:       string;
  broadcasts_deleted:  number;
  items_deleted:       number;
}

export async function deleteMixtape(mixtapeId: string): Promise<DeleteMixtapeResult> {
  const resp = await authedFetch('/mixtapes/delete', {
    method: 'POST',
    body:   JSON.stringify({ mixtape_id: mixtapeId }),
  });
  return resp.json();
}

export interface DeleteMixtapesBulkResult {
  ok: true;
  deleted_count:      number;
  broadcasts_deleted: number;
  items_deleted:      number;
}

export async function deleteMixtapesBulk(mixtapeIds: string[]): Promise<DeleteMixtapesBulkResult> {
  const resp = await authedFetch('/mixtapes/delete-bulk', {
    method: 'POST',
    body:   JSON.stringify({ mixtape_ids: mixtapeIds }),
  });
  return resp.json();
}
